import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { API_BASE } from '@/config/api';

const API_URL = API_BASE;

// Configure axios defaults
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useStore = create(
  persist(
    (set, get) => ({
      // Auth state
      user: null,
      token: null,
      isAuthenticated: false,
      
      // UI state
      language: localStorage.getItem('language') || 'en',
      
      // Game state
      currentGame: null,
      availableGames: [],
      
      // Auth actions
      login: async (email, password) => {
        try {
          const response = await axios.post(`${API_URL}/auth/login`, { email, password });
          const { access_token, user } = response.data;
          localStorage.setItem('token', access_token);
          set({ user, token: access_token, isAuthenticated: true });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Login failed' };
        }
      },
      
      register: async (email, password, username, language, referralCode) => {
        try {
          const response = await axios.post(`${API_URL}/auth/register`, { 
            email, password, username, language,
            ...(referralCode ? { referral_code: referralCode } : {})
          });
          const { access_token, user } = response.data;
          localStorage.setItem('token', access_token);
          localStorage.removeItem('ref_code');
          set({ user, token: access_token, isAuthenticated: true });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Registration failed' };
        }
      },

      // Telegram authentication
      telegramLogin: async (initData) => {
        try {
          const response = await axios.post(`${API_URL}/auth/telegram`, { init_data: initData });
          const { access_token, user, telegram_user } = response.data;
          localStorage.setItem('token', access_token);
          set({ 
            user, 
            token: access_token, 
            isAuthenticated: true,
            telegramUser: telegram_user
          });
          return { success: true, user, telegram_user };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Telegram auth failed' };
        }
      },

      // Telegram user data
      telegramUser: null,
      
      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false, currentGame: null, telegramUser: null });
      },
      
      fetchUser: async () => {
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          set({ user: response.data, isAuthenticated: true });
          return response.data;
        } catch (error) {
          get().logout();
          return null;
        }
      },
      
      // Language actions
      setLanguage: async (lang) => {
        localStorage.setItem('language', lang);
        set({ language: lang });
        if (get().isAuthenticated) {
          try {
            await axios.put(`${API_URL}/users/language?language=${lang}`);
          } catch (e) {
            console.error('Failed to update language on server');
          }
        }
      },
      
      // Game actions
      fetchAvailableGames: async () => {
        try {
          const response = await axios.get(`${API_URL}/games`);
          set({ availableGames: response.data });
          return response.data;
        } catch (error) {
          console.error('Failed to fetch games:', error);
          return [];
        }
      },
      
      createGame: async (stake, timeControl, increment, myColor = "random") => {
        try {
          const response = await axios.post(`${API_URL}/games`, {
            stake, time_control: timeControl, increment, my_color: myColor
          });
          set({ currentGame: response.data });
          return { success: true, game: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to create game' };
        }
      },
      
      joinGame: async (gameId) => {
        try {
          const response = await axios.post(`${API_URL}/games/${gameId}/join`);
          set({ currentGame: response.data });
          return { success: true, game: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to join game' };
        }
      },
      
      fetchGame: async (gameId) => {
        try {
          const response = await axios.get(`${API_URL}/games/${gameId}`);
          set({ currentGame: response.data });
          return response.data;
        } catch (error) {
          return null;
        }
      },
      
      makeMove: async (gameId, move) => {
        try {
          const response = await axios.post(`${API_URL}/games/${gameId}/move`, {
            game_id: gameId, move
          });
          if (response.data.success) {
            set({ currentGame: response.data.game });
          }
          return response.data;
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Invalid move' };
        }
      },
      
      resignGame: async (gameId) => {
        try {
          const response = await axios.post(`${API_URL}/games/${gameId}/resign`);
          return response.data;
        } catch (error) {
          return { success: false, error: error.response?.data?.detail };
        }
      },
      
      setCurrentGame: (game) => set({ currentGame: game }),
      
      // Wallet actions
      getBalance: async () => {
        try {
          const response = await axios.get(`${API_URL}/wallet/balance`);
          return response.data.balance;
        } catch (error) {
          return 0;
        }
      },
      
      getTransactions: async () => {
        try {
          const response = await axios.get(`${API_URL}/wallet/transactions`);
          return response.data;
        } catch (error) {
          return [];
        }
      },
      
      deposit: async (amount, paymentMethod = 'card') => {
        try {
          const response = await axios.post(`${API_URL}/wallet/deposit`, {
            amount, 
            currency: 'RUB',
            payment_method: paymentMethod
          });
          // Don't refresh user data here - will be refreshed after payment completion
          // Only refresh if no redirect URL (mock mode)
          if (!response.data.payment_url) {
            await get().fetchUser();
          }
          return { 
            success: true, 
            payment_url: response.data.payment_url,
            mock: response.data.mock,
            ...response.data 
          };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Deposit failed' };
        }
      },
      
      withdraw: async (amount, cardNumber) => {
        try {
          const response = await axios.post(`${API_URL}/wallet/withdraw`, {
            amount, card_number: cardNumber
          });
          await get().fetchUser();
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Withdrawal failed' };
        }
      },
      
      // KYC actions
      startKyc: async () => {
        try {
          const response = await axios.post(`${API_URL}/kyc/start`);
          return response.data;
        } catch (error) {
          return { success: false, error: error.response?.data?.detail };
        }
      },
      
      approveKycMock: async () => {
        try {
          const response = await axios.post(`${API_URL}/kyc/approve-mock`);
          await get().fetchUser();
          return response.data;
        } catch (error) {
          return { success: false, error: error.response?.data?.detail };
        }
      },
      
      // Tournament actions
      fetchTournaments: async (status = null) => {
        try {
          const url = status ? `${API_URL}/tournaments?status=${status}` : `${API_URL}/tournaments`;
          const response = await axios.get(url);
          return response.data;
        } catch (error) {
          return [];
        }
      },
      
      joinTournament: async (tournamentId) => {
        try {
          const response = await axios.post(`${API_URL}/tournaments/${tournamentId}/join`);
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to join' };
        }
      },
      
      // Leaderboard
      fetchLeaderboard: async () => {
        try {
          const response = await axios.get(`${API_URL}/leaderboard`);
          return response.data;
        } catch (error) {
          return [];
        }
      },
      
      // Game history
      fetchGameHistory: async () => {
        try {
          const response = await axios.get(`${API_URL}/games/history/me`);
          return response.data;
        } catch (error) {
          return [];
        }
      }
    }),
    {
      name: 'chess-platform-storage',
      partialize: (state) => ({ 
        token: state.token, 
        language: state.language 
      })
    }
  )
);

export default useStore;
